import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LuUser, LuBriefcase, LuLandmark, LuActivity, LuPhone, LuMail, LuGlobe, LuMapPin } from 'react-icons/lu';
import { getLobbyistById } from '../features/lobby/lobbySlice';
import MainLoader from '../components/Structures/MainLoader';
import EngagementsTable from '../components/lobby/EngagementsTable';
import {
  ProfileLayout,
  Panel,
  SectionLabel,
  DetailRow,
  MissingState,
  YearTabs,
} from '../components/lobby/ProfileLayout';
import {
  formatDate,
  formatCompensation,
  hasCompensation,
  lobbyistName,
  StatusBadge,
  yearsOf,
} from '../components/lobby/lobbyFormat';

const Lobbyist = () => {
  const { id, state } = useParams();
  const dispatch = useDispatch();
  const { isLoading, isError, lobbyist } = useSelector((s) => s.lobby);
  const [year, setYear] = useState(null);

  useEffect(() => {
    if (id) dispatch(getLobbyistById(id));
  }, [id, dispatch]);

  const data = Array.isArray(lobbyist) ? lobbyist[0] : null;
  const loaded = data && String(data.LobbyistID) === String(id);
  const clients = useMemo(() => (loaded ? data.clients || [] : []), [loaded, data]);
  const years = useMemo(() => yearsOf(clients), [clients]);
  const activeYear = year ?? years[0] ?? 'all';
  const visible = activeYear === 'all' ? clients : clients.filter((c) => c.Year === activeYear);

  useEffect(() => setYear(null), [id]);

  if (isLoading || (!loaded && !isError)) return <MainLoader label="Loading lobbyist…" />;
  if (!loaded) {
    return <MissingState title="Lobbyist not found" message={`We couldn't find this lobbyist in ${state?.toUpperCase()}.`} />;
  }

  const name = lobbyistName(data);
  const firm = data.FirmName || data.LobbyistBusiness;
  const address = [
    data.LobbyistAddress1,
    data.LobbyistAddress2,
    data.LobbyistCity,
    [data.LobbyistState, data.LobbyistZip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  const activeClients = new Set(
    clients.filter((c) => c.LobbyEngagementStatus === 'Active').map((c) => c.CompanyID)
  ).size;

  const columns = [
    {
      key: 'CompanyName',
      label: 'Client',
      render: (r) => (
        <Link to={`/${state}/company/${r.CompanyID}`} className="font-medium text-info hover:text-info-strong">
          {r.CompanyName}
        </Link>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (r) => [r.CompanyCity, r.CompanyState].filter(Boolean).join(', ') || '—',
      sortValue: (r) => r.CompanyCity,
      className: 'text-muted-foreground',
    },
    { key: 'Year', label: 'Year' },
    { key: 'BeginDate', label: 'Begins', className: 'whitespace-nowrap', render: (r) => formatDate(r.BeginDate) },
    { key: 'StopDate', label: 'Ends', className: 'whitespace-nowrap', render: (r) => formatDate(r.StopDate) },
    { key: 'LobbyEngagementStatus', label: 'Status', render: (r) => <StatusBadge status={r.LobbyEngagementStatus} /> },
    ...(hasCompensation(visible)
      ? [{ key: 'comp', label: 'Compensation', align: 'right', render: formatCompensation, sortValue: (r) => r.Exact ?? r.SalaryLow }]
      : []),
  ];

  const rail = (
    <Panel>
      <div className="space-y-5 p-5">
        <SectionLabel>Lobbyist</SectionLabel>
        <div className="flex items-start gap-4">
          {data.imageUrl ? (
            <img src={data.imageUrl} alt="" className="h-20 w-16 shrink-0 rounded-md border border-border object-cover" />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
              <LuUser className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-semibold leading-tight tracking-tight">{name}</h1>
            {data.LobbyistTitle && <p className="mt-0.5 text-sm text-muted-foreground">{data.LobbyistTitle}</p>}
          </div>
        </div>
        <div className="space-y-3 border-t border-border pt-4">
          {firm && <DetailRow icon={LuBriefcase} label="Firm">{firm}</DetailRow>}
          {data.LobbyistRegisteredBranches && (
            <DetailRow icon={LuLandmark} label="Branches">{data.LobbyistRegisteredBranches}</DetailRow>
          )}
          <DetailRow icon={LuActivity} label="Status">
            {data.LobbyistStatus ? <StatusBadge status={data.LobbyistStatus} /> : activeClients ? <StatusBadge status="Active" /> : '—'}
          </DetailRow>
        </div>
        <div className="border-t border-border pt-4">
          <SectionLabel>Active clients</SectionLabel>
          <p className="mt-1 text-lg font-semibold tabular-nums">{activeClients}</p>
          <p className="text-xs text-muted-foreground">{clients.length} engagements on file</p>
        </div>
      </div>
      {(data.LobbyistPhone || data.LobbyistEmail || data.LobbyistWebsite || address) && (
        <div className="space-y-3 border-t border-border p-5">
          <SectionLabel>Contact</SectionLabel>
          {data.LobbyistEmail && (
            <a href={`mailto:${data.LobbyistEmail}`} className="flex items-center gap-2 text-sm text-info hover:text-info-strong">
              <LuMail className="h-4 w-4" aria-hidden="true" /> {data.LobbyistEmail}
            </a>
          )}
          {data.LobbyistPhone && (
            <a href={`tel:${data.LobbyistPhone}`} className="flex items-center gap-2 text-sm text-info hover:text-info-strong">
              <LuPhone className="h-4 w-4" aria-hidden="true" /> {data.LobbyistPhone}
            </a>
          )}
          {data.LobbyistWebsite && (
            <a
              href={/^https?:\/\//i.test(data.LobbyistWebsite) ? data.LobbyistWebsite : `https://${data.LobbyistWebsite}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 break-all text-sm text-info hover:text-info-strong"
            >
              <LuGlobe className="h-4 w-4 shrink-0" aria-hidden="true" /> {data.LobbyistWebsite}
            </a>
          )}
          {address && (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <LuMapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {address}
            </p>
          )}
        </div>
      )}
    </Panel>
  );

  return (
    <ProfileLayout crumb={name} rail={rail}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-lg font-semibold">Clients &amp; engagements</h2>
        <YearTabs years={years} value={activeYear} onChange={setYear} />
      </div>
      <Panel className="p-4 sm:p-5">
        <EngagementsTable
          columns={columns}
          rows={visible}
          defaultSort={{ key: 'CompanyName', dir: 'asc' }}
          getRowKey={(r) => r.LobbyingEngagementID}
          emptyMessage="No registered clients for this lobbyist."
        />
      </Panel>
    </ProfileLayout>
  );
};

export default Lobbyist;
